// Klinik Yönetimi (Yönetim › Klinik). Sekmeler: Görünüm (clinic:appearance),
// Modüller (clinic:update — kullanılmayan modülleri kapatma, terim seti),
// Hizmetler ve Paketler (services:manage), WhatsApp (whatsapp:manage).
// Eski `?tab=gorunum` bağlantıları aynı içeriği açmaya devam eder.

import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Settings as SettingsIcon, Palette as PaletteIcon, Extension as ModulesIcon } from "@mui/icons-material";
import { MedicalServicesOutlined as ServicesIcon, WhatsApp as WhatsAppIcon } from "@mui/icons-material";
import { useClinicModule } from "../../hooks/useClinicModule";
import ServiceCatalogPanel from "../../components/organisms/Billing/ServiceCatalogPanel";
import { useClinicTheme } from "../../theme";
import useAuth from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { TabbedPageShell } from "../../components/organisms";
import ClinicModulesPanel from "../../components/organisms/ClinicModulesPanel";
import AppearanceTab from "../Clinics/ClinicDetail/AppearanceTab";
import WhatsAppTab from "./WhatsAppTab";
import {
  useGetMyClinicModulesMutation,
  useUpdateMyClinicModulesMutation,
} from "../../store/clinicApi";

function MyClinicModules({ token, palette, canEdit }) {
  const { showSuccess, showError } = useAlert();
  const { refreshMe } = useAuth();
  const [getModules, { isLoading }] = useGetMyClinicModulesMutation();
  const [updateModules, { isLoading: saving }] = useUpdateMyClinicModulesMutation();
  const [modules, setModules] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await getModules({ token }).unwrap();
      setModules(r?.data?.modules || null);
    } catch (e) {
      showError(e?.data?.message || "Modüller yüklenemedi");
    }
  }, [getModules, token, showError]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const handleSave = async (payload) => {
    try {
      const r = await updateModules({ token, ...payload }).unwrap();
      setModules(r?.data?.modules || null);
      showSuccess("Modüller güncellendi");
      // Menü ve sekmeler etkin izinlerden çizilir — oturumu tazele.
      refreshMe?.();
    } catch (e) {
      showError(e?.data?.message || "Modüller güncellenemedi");
    }
  };

  return (
    <ClinicModulesPanel
      mode="clinic"
      modules={modules}
      loading={isLoading && !modules}
      saving={saving}
      canEdit={canEdit}
      palette={palette}
      onSave={handleSave}
    />
  );
}

export default function ClinicManagement() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const clinicId = auth?.clinic?._id || auth?.user?.clinic?._id || auth?.clinic || null;
  const canAppearance = useHasPermission("clinic:appearance");
  const canModules = useHasPermission("clinic:update");
  // Hizmetler ve Paketler (billing modülü): izin modül kapalıysa sunucuda zaten düşer
  const hasServicesPerm = useHasPermission("services:manage");
  const billingActive = useClinicModule("billing");
  const canServices = hasServicesPerm && billingActive;
  // WhatsApp (whatsapp modülü): izin modül kapalıyken sunucuda zaten süzülür
  const canWhatsApp = useHasPermission("whatsapp:manage");

  const tabs = [
    canAppearance && { key: "gorunum", label: "Görünüm", icon: <PaletteIcon sx={{ fontSize: 18 }} /> },
    canModules && { key: "moduller", label: "Modüller", icon: <ModulesIcon sx={{ fontSize: 18 }} /> },
    canServices && { key: "hizmetler", label: "Hizmetler ve Paketler", icon: <ServicesIcon sx={{ fontSize: 18 }} /> },
    canWhatsApp && { key: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon sx={{ fontSize: 18 }} /> },
  ].filter(Boolean);

  return (
    <TabbedPageShell
      icon={<SettingsIcon sx={{ fontSize: 24 }} />}
      title="Klinik Yönetimi"
      subtitle="Kliniğin görünümü ve kullandığı modüller"
      tabs={tabs}
    >
      {(tab) => (
        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          {tab === "moduller" ? (
            <MyClinicModules token={token} palette={palette} canEdit={canModules} />
          ) : tab === "hizmetler" ? (
            <ServiceCatalogPanel token={token} canManage={canServices} />
          ) : tab === "whatsapp" ? (
            <WhatsAppTab token={token} palette={palette} />
          ) : (
            <AppearanceTab apiMode="clinic" clinicId={clinicId} token={token} palette={palette} />
          )}
        </Box>
      )}
    </TabbedPageShell>
  );
}
